import React, { useContext } from "react";
import OrderSort from "@/scenes/OrderManagement/enum/OrderSort";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import OrderManagementStore, {
    buildListOrdersParams,
    OrderManagementContext,
} from "@/scenes/OrderManagement/store/OrderManagementStore";
import {
    getMultiShopOrderCount,
    getOrderListCount,
    list,
    OrderListCount,
    QueryOrderListParams,
} from "@/services/api/order";
import type GlobalStore from "@/store";
import { Modal } from "antd";
import { observable, makeObservable, override, actionBound } from "mobx";
import { chunk, uniqBy } from "lodash";
import { concurrentQueue, uuid } from "@/utils";
import MultiShopSelectStore from "@/components/MultiShopSelect/MultiShopSelectStore";
import { getAllShopDataByOffsetPaginationByConcurrent } from "@/utils/getAllShopDataByPagination";
import ProcessingModal from "@/scenes/OrderManagement/components/dialogs/ProcessingModal";
import getOrdersByLocalSort from "@/scenes/OrderManagement/utils/getOrdersByLocalSort";
import OrderDTO from "@/dto/order/OrderDTO";
import FetchOrderErrorModal from "../components/dialogs/FetchOrderErrorModal";
import { ShopFetchOrderError } from "../components/dialogs/FetchOrderErrorModal/FetchOrderErrorTable";
import { getShopRequestParamsMapping } from "./ShopRequestParamsBuilder";

export const MULT_PLATFORM_FETCH_ORDER_EVENT = "MULT_PLATFORM_FETCH_ORDER_EVENT";

export function useOrderManagementStore() {
    return (useContext(OrderManagementContext) as unknown) as OrderManagementForAllShopStore;
}

async function getCurrentMultiShopOrderCount(
    globalStore: typeof GlobalStore,
    shopIds: number[],
    params: any,
    /** 如果有联动搜索相关的参数，则会有此参数 */
    shopParamsMapping: Map<number, QueryOrderListParams>,
    errorShopMapping?: Map<number, string>,
) {
    const totalCountMapping = new Map<number, number>();

    if (shopParamsMapping) {
        await concurrentQueue({
            data: shopIds,
            maxTask: 5,
            async callback(shopId) {
                const shopParams = shopParamsMapping.get(shopId);

                if (!shopParams) return;

                try {
                    const results = await getMultiShopOrderCount({
                        ...shopParams,
                        shopIds: [shopId],
                    });

                    results.shopOrderCountList.forEach((shopOrderCount) => {
                        totalCountMapping.set(shopOrderCount.shopId, shopOrderCount.totalOrders);
                    });
                } catch (error) {
                    errorShopMapping?.set(shopId, error?.message);
                }
            },
        });
    } else {
        const tbShops: number[] = [];
        const pddShops: number[] = [];
        const dyShops: number[] = [];
        const jdShops: number[] = [];
        const aliShops: number[] = [];
        const xhsShops: number[] = [];
        const channelShops: number[] = [];
        const wbShops: number[] = [];
        const otherShops: number[] = [];

        shopIds.forEach((shopId) => {
            const shop = globalStore.shopMapping.get(shopId);

            if (MultiShopSelectStore.tbShopTypes.includes(shop.type)) {
                tbShops.push(shopId);
            } else if (MultiShopSelectStore.pddShopTypes.includes(shop.type)) {
                pddShops.push(shopId);
            } else if (MultiShopSelectStore.dyShopTypes.includes(shop.type)) {
                dyShops.push(shopId);
            } else if (MultiShopSelectStore.jdShopTypes.includes(shop.type)) {
                jdShops.push(shopId);
            } else if (MultiShopSelectStore.aliShopTypes.includes(shop.type)) {
                aliShops.push(shopId);
            } else if (MultiShopSelectStore.xhsShopTypes.includes(shop.type)) {
                xhsShops.push(shopId);
            } else if (MultiShopSelectStore.channelShopTypes.includes(shop.type)) {
                channelShops.push(shopId);
            } else if (MultiShopSelectStore.wbShopTypes.includes(shop.type)) {
                wbShops.push(shopId);
            } else {
                otherShops.push(shopId);
            }
        });
        const shopIdGroups = [
            tbShops,
            pddShops,
            dyShops,
            jdShops,
            aliShops,
            xhsShops,
            channelShops,
            wbShops,
            otherShops,
        ];

        const waitToLoadShopIdsChunk: number[][] = [];

        for (let i = 0; i < shopIdGroups.length; i++) {
            const shopIdsGroup = shopIdGroups[i];

            const shopIdsChunk = chunk(shopIdsGroup, 10);

            for (let j = 0; j < shopIdsChunk.length; j++) {
                const shopIds = shopIdsChunk[j];

                waitToLoadShopIdsChunk.push(shopIds);
            }
        }

        await concurrentQueue({
            data: waitToLoadShopIdsChunk,
            maxTask: 5,
            async callback(shopIds) {
                try {
                    const results = await getMultiShopOrderCount({
                        ...params,
                        shopIds,
                    });
                    for (let k = 0; k < results.shopOrderCountList.length; k++) {
                        const result = results.shopOrderCountList[k];
                        totalCountMapping.set(result.shopId, result.totalOrders);
                    }
                } catch (error) {
                    for (let k = 0; k < shopIds.length; k++) {
                        const shopId = shopIds[k];
                        errorShopMapping?.set(shopId, error?.message);
                    }
                }
            },
        });
    }

    return { totalCountMapping };
}

export default class OrderManagementForAllShopStore extends OrderManagementStore {
    constructor(public globalStore: typeof GlobalStore) {
        super(globalStore);

        makeObservable(this, {
            shopIds: observable,
            orderListCountForAllShop: observable,
            init: actionBound,
            updateShopIds: actionBound,
            getOrderListCount: override,
            fetchOrders: override,
        });

        this.init();
    }

    shopIds: number[];

    orderListCountForAllShop?: OrderListCount;

    async init() {
        await this.table.getOrderTableConfig(undefined, OrderTab.PENDING_SHIPMENT);

        if (!this.table.listOptions.orderSort) {
            this.table.listOptions.orderSort = OrderSort.ORDER_TIME_DESC;
        }

        this.table.setOrders(null);
    }

    updateShopIds(shopIds: number[]) {
        this.shopIds = shopIds;
    }

    async getOrderListCount() {
        if (!this.shopIds?.length || !this.tab) {
            this.orderListCountForAllShop = undefined;

            return;
        }

        let requestId = uuid();

        this.lastRequestId = requestId;
        const results: OrderListCount[] = [];

        await concurrentQueue({
            data: this.shopIds,
            callback: async (shopId) => {
                const result = await getOrderListCount({
                    shopId,
                    tab: this.tab,
                });

                results.push(result);
            },
        });

        if (this.lastRequestId === requestId) {
            this.orderListCountForAllShop = results.reduce<OrderListCount>(
                (total, current) => {
                    Object.keys(current).forEach((key) => {
                        if (typeof current[key] === "boolean") {
                            if (current[key]) {
                                total[key] = true;
                            }
                        } else if (typeof current[key] === "number") {
                            total[key] = (total[key] || 0) + (current[key] || 0);
                        }
                    });

                    return total;
                },
                {
                    totalPendingShipmentOrders: 0,
                    totalOnHoldOrders: 0,
                    totalRefundingOrders: 0,
                    totalPendingShipmentRefundingOrders: 0,
                    totalShippedRefundingOrders: 0,
                    totalPendingApprovalOrders: 0,
                    totalProcessingOrders: 0,
                    hasAbnormalOrders: false,
                    totalLogisticsWarningOrders: 0,
                    preShipOrders: 0,
                    shippingDeadlinePendingShipmentOrderCount: 0,
                    shippingDeadlineOnHoldOrderCount: 0,
                    shippingDeadlineRefundingOrderCount: 0,
                    shippingOverduePendingShipmentOrderCount: 0,
                    shippingOverdueOnHoldOrderCount: 0,
                    shippingOverdueRefundingOrderCount: 0,
                },
            );

            console.log(JSON.parse(JSON.stringify(this.orderListCountForAllShop)));
        }

        return this.orderListCountForAllShop;
    }

    async fetchOrders() {
        const values = this.table.form.getFieldsValue();
        const shopIds = values.shopIds;

        if (!shopIds?.length) {
            Modal.error({
                title: "提示",
                content: "请先选择店铺再搜索订单",
            });
            return;
        }

        console.time("all fetch orders");

        let processingModalInstance = undefined;

        try {
            const tab = this.tab || OrderTab.PENDING_SHIPMENT;

            await this.table.getOrderTableConfig(undefined, tab);

            // 然后拿取本地配置 请求数据
            const params = await buildListOrdersParams(values);
            delete params["shopIds"];
            if (this.table.listOptions?.orderSort) {
                params["orderSort"] = this.table.listOptions.orderSort;
            }
            if (this.table.listOptions?.fulfillmentType) {
                params["fulfillmentType"] = this.table.listOptions.fulfillmentType;
            }
            params["tab"] = tab;

            this.collectData(params);

            const shopParamsMapping = getShopRequestParamsMapping({
                globalStore: this.globalStore,
                shopIds,
                rawParams: params,
                supplierPartnershipsStore: this.supplierPartnershipsStore,
            });

            this.table.prevQueryParams = JSON.stringify(params);

            if (!this.table.pagination.pageNo) {
                this.table.pagination.pageNo = 1;
            }
            let pageNo = this.table.pagination.pageNo;
            let pageSize = this.table.pagination.pageSize;

            processingModalInstance = this.globalStore.modalStore.openModal(ProcessingModal, {
                current: 0,
                count: pageSize,
                text: "订单加载中...",
            });

            const errorShopMapping = new Map<number, string>();

            console.time("load orders");

            const { totalCountMapping } = await getCurrentMultiShopOrderCount(
                this.globalStore,
                shopIds,
                params,
                shopParamsMapping,
                errorShopMapping,
            );

            let orders: OrderDTO[] = [];
            const shopCount = totalCountMapping.size;
            this.table.pagination.shopCount = shopCount;

            let totalCount = 0;
            totalCountMapping.forEach((c) => {
                totalCount += c;
            });

            this.table.pagination.totalCount = totalCount;

            orders = await getAllShopDataByOffsetPaginationByConcurrent({
                shopIds,
                shopTotalCountMapping: totalCountMapping,
                pageNo,
                pageSize,
                maxLimit: 200,
                onShopLoadError(shopId, error) {
                    console.error(error);

                    errorShopMapping.set(shopId, error?.message);
                },
                async onLoadShopData(shopId: number, offset: number, limit: number) {
                    const _params = shopParamsMapping?.get(shopId) || params;

                    const result = await list(
                        {
                            ..._params,
                            shopId,
                            offset,
                            limit,
                            needTotalCount: false,
                        },
                        {
                            hideModal: true,
                        },
                    );

                    return result.orders;
                },
                onLoadedCountChange(count) {
                    processingModalInstance.update({
                        current: count,
                    });
                },
            });

            orders = uniqBy(orders, (order) => `${order.shopId}_${order.id}`);

            console.timeEnd("load orders");

            console.time("set orders");

            this.table.setOrders(orders);
            // 如果有本地排序，需要重新设置一下排序后的订单到列表中，但是设置后的订单不能作为源订单
            if (this.table?.listOptions?.localOrderSort) {
                const newOrders = getOrdersByLocalSort(orders, this.table.listOptions.localOrderSort);

                this.table.setOrders(newOrders, false);
            }

            console.timeEnd("set orders");

            // 如果有请求错误的店铺
            if (errorShopMapping.size) {
                const dataSource: ShopFetchOrderError[] = [];

                errorShopMapping.forEach((message, shopId) => {
                    dataSource.push({
                        shopId,
                        message,
                    });
                });

                this.globalStore.modalStore.openModal(FetchOrderErrorModal, { dataSource });
            }

            const event = new Event(MULT_PLATFORM_FETCH_ORDER_EVENT);
            window.dispatchEvent(event);
        } catch (error) {
            console.error(error);

            this.table.setOrders([]);
        } finally {
            if (processingModalInstance) {
                processingModalInstance.onCancel();
            }

            console.timeEnd("all fetch orders");

            console.time("set lock orders");

            await this.table.orderLockStore.handleLockOrders(this.table.orders);

            console.timeEnd("set lock orders");
        }

        const uniqShopIds = [...new Set(this.table.orders.map(({ shopId }) => shopId))];

        /** 缺少的店铺设置 */
        const needAddShopIds = uniqShopIds.filter((shopId) => !this.globalStore.shopSettingMapping.has(shopId));

        this.globalStore.getShopSetting(needAddShopIds);

        this.table.loadManualMergeableOrderCount(this.table.orders, this.tab);
    }
}
